import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VirtualList from 'react-tiny-virtual-list';
import AutoSizer from 'react-virtualized-auto-sizer';

import { TemplatePlayer } from '../../types/team';
import { Paginator } from '../Paginator';

import { ItemAssistant, VariantsAssistance } from './ItemsAssistant';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { SelectValue } from '@/types';
import { JugFigura } from '@/types/fieldAssistance';
import {
  EMPTY_TEMPLATE_PLAYER,
  FADE_IN_OUT,
  sortAlphabeticByLastName,
} from '@/utils';

interface Props extends VariantsAssistance {
  dataPlayersProps: JugFigura[];
  positionSelected: SelectValue;
}

export const TopPlayersTable = ({
  dataPlayersProps,
  positionSelected,
  variant = 'DEFAULT',
}: Props) => {
  const vlistRef = useRef<VirtualList>(null);
  const playerTemplateMap = usePlayerTemplateMap();
  const { isMobile } = useIsDesktop();
  const [contentCurrentPage, setContentCurrentPage] = useState<
    TemplatePlayer[]
  >([]);

  useEffect(() => {
    if (!vlistRef.current) return;
    vlistRef.current.scrollTo(0);
  }, [positionSelected.value]);

  const fieldPlayersMap = useMemo(() => {
    const map = new Map<number, JugFigura>();
    dataPlayersProps.forEach((player) => {
      map.set(player.idJT, player);
    });
    return map;
  }, [dataPlayersProps]);

  const playersFiltered = useMemo(() => {
    const players: TemplatePlayer[] = dataPlayersProps.map((player) => {
      return playerTemplateMap.get(player.idJT) || EMPTY_TEMPLATE_PLAYER;
    });

    const filteredPlayers: TemplatePlayer[] = players.flatMap(
      (player: TemplatePlayer) => {
        const matchesPosition =
          positionSelected.value === 0 || player?.p === positionSelected.value;

        if (matchesPosition) {
          return [player];
        }
        return [];
      }
    );

    // Sort by number of times as figura (valorReporte) descending, then alphabetically
    return filteredPlayers.sort((a, b) => {
      const figuraA = fieldPlayersMap.get(a.id)?.valorReporte || 0;
      const figuraB = fieldPlayersMap.get(b.id)?.valorReporte || 0;
      if (figuraB !== figuraA) {
        return figuraB - figuraA;
      }
      // If same figura count, sort alphabetically
      return sortAlphabeticByLastName([a, b])[0] === a ? -1 : 1;
    });
  }, [
    playerTemplateMap,
    dataPlayersProps,
    positionSelected.value,
    fieldPlayersMap,
  ]);

  const getDataFieldPlayer = useCallback(
    (id: number): JugFigura | undefined => {
      return fieldPlayersMap.get(id);
    },
    [fieldPlayersMap]
  );

  if (isMobile) {
    return (
      playersFiltered &&
      playersFiltered.length > 0 && (
        <div className="h-screen mt-5 rounded-none bg-white">
          <AutoSizer className="h-full lg:h-[1002px]">
            {({ height, width }) => {
              return (
                <VirtualList
                  ref={vlistRef}
                  height={height}
                  itemCount={playersFiltered.length}
                  itemSize={80}
                  renderItem={({ index, style }) => {
                    const player = playersFiltered[index];
                    if (player) {
                      return (
                        <div key={index} style={style}>
                          <ItemAssistant.TopPlayers
                            dataPlayerFieldAssistance={getDataFieldPlayer(
                              player.id
                            )}
                            templatePlayer={player}
                            variant={variant}
                          />
                        </div>
                      );
                    }
                  }}
                  width={width}
                />
              );
            }}
          </AutoSizer>
        </div>
      )
    );
  }

  const handleContentCurrentPage = (players: TemplatePlayer[]) => {
    players && setContentCurrentPage(players);
  };

  if (!Array.isArray(playersFiltered) || playersFiltered.length === 0) {
    return null;
  }

  const itemsToRender =
    Array.isArray(contentCurrentPage) && contentCurrentPage.length > 0
      ? contentCurrentPage
      : playersFiltered;

  return (
    <motion.div
      key={variant}
      animate="show"
      initial="hide"
      variants={FADE_IN_OUT}
    >
      <div className="lg:h-auto mt-6 rounded-none lg:rounded-lg bg-white">
        {itemsToRender.map((player: TemplatePlayer, index: number) =>
          player ? (
            <ItemAssistant.TopPlayers
              key={`${player.id ?? 'p'}-${index}`}
              dataPlayerFieldAssistance={getDataFieldPlayer(player.id)}
              templatePlayer={player}
              variant={variant}
            />
          ) : (
            ''
          )
        )}

        {playersFiltered.length > 10 && (
          <Paginator
            key={`top-players-${positionSelected?.label ?? 'none'}`}
            currentContentPage={handleContentCurrentPage}
            items={playersFiltered}
          />
        )}
      </div>
    </motion.div>
  );
};
